import { InsuranceProvider, PopulatedAssessment } from '../../types'
import { AssessmentForm } from '../../types/assessments/form'
import {
  getHasPrimaryCoverage,
  getHasSecondaryCoverage,
  getPrimaryIsMedicaid,
  getSecondaryIsMedicaid,
} from '../../utils'
import { deliveryStage } from './delivery'
import { getInsuranceInfoStage } from './insuranceInfo'

export const getIntroductionForm = (
  admin: boolean,
  assessment?: Omit<PopulatedAssessment, 'availableForms' | 'stages' | 'plans'> | null,
): AssessmentForm => ({
  name: 'Introduction',
  description: 'Tell us a little bit about your pregnancy',
  stages: {
    delivery: deliveryStage,
    insuranceInfo: getInsuranceInfoStage(
      admin ? assessment?.mergedData?.insuranceInfo : assessment?.data?.insuranceInfo,
      admin,
    ),
  },
  getNextFormId: (admin, assessment) => {
    const { data, mergedData } = assessment ?? {}

    const insuranceInfoData = admin ? mergedData?.insuranceInfo : data?.insuranceInfo

    const primaryCoverage = insuranceInfoData?.primaryCoverage

    const secondaryCoverage = insuranceInfoData?.secondaryCoverage

    const { insuranceProvider: primaryInsurer } = primaryCoverage ?? {}
    const { insuranceProvider: secondaryInsurer } = secondaryCoverage ?? {}

    const option = insuranceInfoData?.option
    const coverageWillChangeOption = insuranceInfoData?.coverageWillChangeDescription
    const noCoverageOption = insuranceInfoData?.noCoverageDescription
    const multipleOptionsOption = insuranceInfoData?.multipleOptionsDescription
    const canEnrollInPartnerPlan = insuranceInfoData?.canEnrollInPartnerPlan

    const getCallInForInsurer = (insurer: InsuranceProvider) => {
      if (insurer?.loqNotRequired) return null
      return 'call-in'
    }

    const getCallIn = () => {
      const hasPrimaryCoverage = getHasPrimaryCoverage(insuranceInfoData)
      const primaryIsMedicaid = getPrimaryIsMedicaid(insuranceInfoData)
      if (hasPrimaryCoverage && !primaryIsMedicaid) return getCallInForInsurer(primaryInsurer)
      const secondaryIsMedicaid = getSecondaryIsMedicaid(insuranceInfoData)
      const hasSecondaryCoverage = getHasSecondaryCoverage(insuranceInfoData)
      if (hasSecondaryCoverage && !secondaryIsMedicaid) return getCallInForInsurer(secondaryInsurer)
      return null
    }

    if (!option) return null
    switch (option) {
      case 'one-plan':
        return getCallIn()
      case 'two-plans':
        return getCallIn()
      case 'medicaid':
        return null
      case 'coverage-will-change':
        switch (coverageWillChangeOption) {
          case 'employment-coverage-will-change':
            return getCallIn()
          case 'employment-coverage-will-change-unknown':
            return 'schedule-call'
          case 'coverage-will-drop':
            return 'plan-options'
          case 'added-to-another-plan':
            return getCallIn()
          default:
            return null
        }
      case 'multiple-options':
        switch (multipleOptionsOption) {
          case 'partner-individual':
            return canEnrollInPartnerPlan ? 'call-in' : null
          case 'partner-employer':
            return getCallIn()
          default:
            return null
        }
      case 'not-pregnant':
        return 'plan-options'
      case 'no-coverage':
        if (noCoverageOption === 'out-of-pocket') {
          return null
        }
        return 'plan-options'
      default:
        return null
    }
  },
})
